import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { discardDecision, keepDecidedFrame, readDecision } from '@/lib/capture-frames';
import type { CaptureFrame } from '@/lib/capture-frames';

const FAULT_COPY: Record<string, string> = {
  resolution: 'Too small. Fill the frame.',
  exposure: 'Poor light. Avoid backlight.',
  sharpness: 'Soft image. Hold steady.',
};

/**
 * CA-04: Quality gate retake (wireframes/capture-app.html #ca04). Named per
 * SC3 note 3, this screen is advisory: "Keep frame" adds it to the
 * capture with the failing `QualityGateResult` attached, because whether a
 * failed gate blocks is open decision B1 and it is a server decision, never
 * a client one.
 *
 * The frame comes from `lib/capture-frames`, which the viewfinder fills
 * before navigating here. This screen used to draw the words "captured frame
 * preview" in place of the photograph and queue `new Blob()` in place of its
 * bytes, so the operator judged a frame they could not see and the capture
 * then failed its whole retry budget on a 400. Both halves came from the same
 * missing thing, which was any route for the pixels to travel.
 *
 * Neither choice sends anything. A capture carries up to three frames and the
 * operator is still building it, so both routes lead back to the viewfinder
 * and the queueing happens there, once, over the whole set.
 */
export function QualityRetake() {
  const navigate = useNavigate();
  const { jobId = '' } = useParams();
  const viewfinderPath = `/jobs/${encodeURIComponent(jobId)}/capture`;
  const [frame] = useState<CaptureFrame | null>(() => readDecision()?.frame ?? null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!frame) {
      // Nothing to decide about, which a reload causes: the bytes lived in
      // memory for one decision. Sending the operator back to the viewfinder
      // is honest, where queueing an empty capture was not.
      navigate(viewfinderPath, { replace: true });
      return;
    }
    const url = URL.createObjectURL(frame.bytes);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [frame, navigate, viewfinderPath]);

  if (!frame) return null;

  const failedChecks = frame.qualityResult.checks.filter((check) => !check.passed);

  function keepAnyway() {
    const outcome = keepDecidedFrame();
    // The operator is standing in front of the item, so the route back is the
    // viewfinder, where the frame now sits in the strip with the others and
    // the whole capture queues in one action. What became of the frame travels
    // with them: a full capture takes no fourth, and saying nothing would send
    // them on believing they had it.
    navigate(viewfinderPath, {
      replace: true,
      // Nothing held is the reload case, which this screen already redirects
      // on, so it carries no message rather than an invented one.
      ...(outcome === 'nothing-held' ? {} : { state: { keptFromRetake: outcome } }),
    });
  }

  function retake() {
    discardDecision();
    navigate(viewfinderPath, { replace: true });
  }

  return (
    /*
      A full-screen route (`isFullScreenRoute`): no tab bar, so both choices
      sit in the bottom third and the frame takes what is left. The frame
      sizes itself from the photograph rather than from a 3:4 box, because a
      capture is whatever shape the operator shot. An operator who has to
      scroll to find Keep frame has been handed the retake by the layout.
    */
    <div className="mx-auto flex min-h-0 w-full max-w-md flex-1 flex-col gap-3 px-4 pt-3 sm:max-w-lg">
      <div className="flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-card border border-border bg-black/20">
        {previewUrl ? (
          <img
            src={previewUrl}
            alt="Flagged frame"
            className="max-h-full w-auto max-w-full object-contain"
          />
        ) : (
          <span className="text-xs text-muted">Loading</span>
        )}
      </div>

      <ul className="flex shrink-0 flex-col gap-1 rounded-card border border-warning px-4 py-3">
        {failedChecks.map((check) => (
          <li key={check.name} className="text-sm font-medium text-warning">
            {FAULT_COPY[check.name] ?? `${check.name} check failed`}
          </li>
        ))}
      </ul>

      <div
        className="flex shrink-0 flex-col gap-2 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
      >
        <Button size="lg" onClick={retake} data-primary-action="">
          Retake
        </Button>
        <Button variant="outline" size="lg" onClick={keepAnyway}>
          Keep frame
        </Button>
      </div>
    </div>
  );
}
